import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as RegistryConstants from 'constants/RegistryConstants';
import {ClientTagSchemaConstraint, UNIQUE_CANONICAL_SMILES_CONSTRAINT} from 'constants/RegistryConstants';
import {TagSchemaItemType, TypeSpecificConstraintType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Modal from 'ui-library/modals/Modal';
import LegacyTagsInput from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {listToReadable} from 'util/NameUtil';

import {ConstraintTableType} from './ConstraintTable';
import TagSchemaConstraintComponent from './TagSchemaConstraint';

export interface SetTagSchemaConstraintModalProps {
  bioentityType: string | null;
  itemType: TagSchemaItemType | null;
  schemaFields: Array<SchemaField | ClientSchemaField>;
  tableType: ConstraintTableType;
  onRequestSubmit: (constraint: ClientTagSchemaConstraint) => void;
  onRequestClose: () => void;
}

interface SetTagSchemaConstraintModalState {
  selectedOptions: Array<Option>;
}

interface Option {
  id: string;
  text: string;
  areUniqueResiduesCaseSensitive?: boolean;
  uniqueResidues?: boolean;
  uniqueCanonicalSmiles?: boolean;
  field?: SchemaField | ClientSchemaField;
  typeSpecificConstraintConfig?: {
    constraintType: TypeSpecificConstraintType;
  };
}

export default class SetTagSchemaConstraintModal extends React.PureComponent<
  SetTagSchemaConstraintModalProps,
  SetTagSchemaConstraintModalState
> {
  override state: SetTagSchemaConstraintModalState = {
    selectedOptions: [],
  };

  _getAllOptions(): Array<Option> {
    const {bioentityType, schemaFields} = this.props;
    const options: Array<Option> = [];

    const uniqueResiduesConstraints = RegistryConstants.SUPPORTED_UNIQUE_RESIDUES_CONSTRAINTS.filter(
      (constraint) => constraint.folderItemType === bioentityType
    );
    options.push(
      ...uniqueResiduesConstraints.map((constraint) => ({
        id: constraint.text,
        text: constraint.text,
        uniqueResidues: true,
        areUniqueResiduesCaseSensitive: constraint.areUniqueResiduesCaseSensitive,
      }))
    );

    if (bioentityType === UNIQUE_CANONICAL_SMILES_CONSTRAINT.folderItemType) {
      options.push({
        id: UNIQUE_CANONICAL_SMILES_CONSTRAINT.text,
        text: UNIQUE_CANONICAL_SMILES_CONSTRAINT.text,
        uniqueCanonicalSmiles: true,
      });
    }

    if (
      this.props.itemType === TagSchemaItemType.ANTIBODY &&
      BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES
    ) {
      const displayName =
        RegistryConstants.TYPE_SPECIFIC_CONSTRAINT_TYPE_TO_DISPLAY_NAME[
          TypeSpecificConstraintType.UNIQUE_ANTIBODY_STRUCTURE
        ]!;
      options.push({
        id: displayName,
        text: displayName,
        typeSpecificConstraintConfig: {
          constraintType: TypeSpecificConstraintType.UNIQUE_ANTIBODY_STRUCTURE,
        },
      });
    }

    schemaFields.forEach((field) => {
      options.push({
        id: field.name,
        text: field.name,
        field,
      });
    });

    return options;
  }

  _handleChangeSelectedOptions = (selectedOptions: Array<Option>): void => {
    this.setState({selectedOptions});
  };

  _handleSubmit = (): void => {
    const {onRequestSubmit} = this.props;
    const {selectedOptions} = this.state;
    const [uniqueResiduesOptions, fieldOptions] = _.partition(selectedOptions, (option) =>
      Boolean(option.uniqueResidues)
    );
    if (uniqueResiduesOptions.length > 1) {
      // Ask user to select only 1 uniqueResidues option
      const uniqueResiduesText = listToReadable(
        uniqueResiduesOptions.map((option) => `"${option.text}"`),
        'or'
      );
      notifications.warn(`Please select only one of ${uniqueResiduesText}.`);
      return;
    }
    const uniqueResidues = Boolean(uniqueResiduesOptions.length);
    const uniqueCanonicalSmiles = selectedOptions.some((option) => Boolean(option.uniqueCanonicalSmiles));
    const typeSpecificConstraintOption = selectedOptions.find((option) =>
      Boolean(option.typeSpecificConstraintConfig)
    );
    const typeSpecificConstraintConfig = typeSpecificConstraintOption?.typeSpecificConstraintConfig;
    onRequestSubmit({
      areUniqueResiduesCaseSensitive: Boolean(
        uniqueResidues && uniqueResiduesOptions[0].areUniqueResiduesCaseSensitive
      ),
      fields: _.compact(fieldOptions.map((option) => option.field)),
      uniqueResidues,
      uniqueCanonicalSmiles,
      typeSpecificConstraintConfig,
    });
  };

  override render(): JSX.Element {
    const {onRequestClose} = this.props;
    const {selectedOptions} = this.state;
    return (
      <Modal
        title="Add a constraint"
        onRequestClose={onRequestClose}
        onRequestSubmit={this._handleSubmit}
        scrollable={false}
        expandHeight={false}
        buttons={[
          {
            text: 'Add',
            disabled: !selectedOptions.length,
            submit: true,
            dataTestAttrs: {component: 'SetTagSchemaConstraintModal', element: 'addButton'},
          },
        ]}
      >
        <div className="u-small-spacing-bottom setTagSchemaConstraintModal-fields">
          <TagSchemaConstraintComponent
            fields={selectedOptions.map((option) => option.text)}
            source={null}
            tableType={this.props.tableType}
          />
        </div>
        <LegacyTagsInput
          allOptions={this._getAllOptions()}
          allowAdding={false}
          onChange={this._handleChangeSelectedOptions}
          placeholder="Set fields"
          size={TagsInputSize.MEDIUM}
          value={selectedOptions}
          dataTestAttrs={{component: 'SetTagSchemaConstraintModal', element: 'input', key: 'field'}}
        />
      </Modal>
    );
  }
}
